import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import CloudQueueRoundedIcon from "@mui/icons-material/CloudQueueRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import ProjectCustomDomainCard from "./ProjectCustomDomainCard";
import { useProjects } from "../../context/ProjectsContext";
import {
  canRetryProjectDeployment,
  getProjectDeploymentMeta,
  getProjectPublicDomain,
  getProjectPublicUrl,
  isProjectDeploymentPending,
} from "../../utils/projectDeployment";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function projectKey(project) {
  return project?.slug || project?.id || project?._id || "";
}

export default function ProjectDeploymentCard({
  project,
  compact = false,
  canRetry = true,
  canManageDomain = true,
  onProjectChange,
}) {
  const { refreshProjectDeployment, retryProjectDeployment } = useProjects();
  const [busy, setBusy] = React.useState("");
  const [localError, setLocalError] = React.useState("");
  const [actionsAnchor, setActionsAnchor] = React.useState(null);
  const compactViewport = useMediaQuery("(max-width:899.95px)");

  const key = projectKey(project);
  const meta = getProjectDeploymentMeta(project);
  const publicUrl = getProjectPublicUrl(project);
  const originalDomain = getProjectPublicDomain({
    deployment: { url: project?.deployment?.url },
  });
  const pending = isProjectDeploymentPending(project);
  const retryAllowed = canRetry && canRetryProjectDeployment(project);
  const compactActions = compact && compactViewport;
  const deploymentError = String(project?.deployment?.errorMessage || "").trim();
  const renderStatus = String(project?.deployment?.lastDeployStatus || "").trim();

  const applyResult = React.useCallback(
    (nextProject) => {
      if (nextProject && typeof onProjectChange === "function") {
        onProjectChange(nextProject);
      }
      return nextProject;
    },
    [onProjectChange]
  );

  const refresh = React.useCallback(
    async ({ silent = false } = {}) => {
      if (!key || busy) return null;
      try {
        if (!silent) setBusy("refresh");
        setLocalError("");
        const nextProject = await refreshProjectDeployment(key);
        return applyResult(nextProject);
      } catch (error) {
        if (!silent) {
          setLocalError(error.message || "No se pudo consultar el estado de la publicación");
        }
        return null;
      } finally {
        if (!silent) setBusy("");
      }
    },
    [applyResult, busy, key, refreshProjectDeployment]
  );

  const retry = React.useCallback(async () => {
    if (!key || busy) return;
    try {
      setBusy("retry");
      setLocalError("");
      const nextProject = await retryProjectDeployment(key);
      applyResult(nextProject);
    } catch (error) {
      setLocalError(error.message || "No se pudo reintentar la publicación");
    } finally {
      setBusy("");
    }
  }, [applyResult, busy, key, retryProjectDeployment]);

  React.useEffect(() => {
    if (!key || !pending) return undefined;

    const timer = window.setInterval(() => {
      refreshProjectDeployment(key)
        .then(applyResult)
        .catch(() => null);
    }, 10000);

    return () => window.clearInterval(timer);
  }, [applyResult, key, pending, refreshProjectDeployment]);

  const openWebsite = () => {
    if (!publicUrl) return;
    window.open(publicUrl, "_blank", "noopener,noreferrer");
  };

  const closeActions = () => setActionsAnchor(null);
  const runRefresh = () => {
    closeActions();
    refresh();
  };
  const runRetry = () => {
    closeActions();
    retry();
  };

  const primaryCompactAction = meta.status === "live" && publicUrl
    ? "open"
    : retryAllowed
      ? "retry"
      : "refresh";
  const primaryCompactLabel = primaryCompactAction === "open"
    ? "Abrir web"
    : primaryCompactAction === "retry"
      ? (meta.status === "not_requested" ? "Publicar" : "Reintentar publicación")
      : "Actualizar estado";
  const showCompactMore = Boolean(
    (primaryCompactAction !== "open" && publicUrl) ||
    primaryCompactAction !== "refresh" ||
    (retryAllowed && primaryCompactAction !== "retry")
  );

  const showCustomDomain = typeof onProjectChange === "function";

  return (
    <Stack spacing={compact ? 1.2 : 1.6} sx={{ minWidth: 0 }}>
      <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: compact ? 1.6 : 2.2,
        borderRadius: compact ? 3 : 4,
        borderColor:
          meta.status === "live"
            ? "rgba(1,142,66,.22)"
            : meta.status === "failed"
              ? "rgba(211,47,47,.22)"
              : "rgba(25,36,47,.10)",
        bgcolor:
          meta.status === "live"
            ? "rgba(1,142,66,.035)"
            : meta.status === "failed"
              ? "rgba(211,47,47,.025)"
              : "rgba(25,36,47,.018)",
      }}
    >
      <Stack spacing={compact ? 1.1 : 1.4}>
        <Stack
          direction={compactActions ? "column" : { xs: "column", sm: "row" }}
          spacing={1.2}
          alignItems={compactActions ? "stretch" : { sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.15} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: compact ? 36 : 42,
                height: compact ? 36 : 42,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: meta.status === "live" ? "rgba(1,142,66,.10)" : "rgba(243,90,58,.10)",
                color: meta.status === "live" ? "success.main" : BRAND.orange,
                flexShrink: 0,
              }}
            >
              {meta.status === "live" ? <CloudDoneRoundedIcon /> : <CloudQueueRoundedIcon />}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Página pública
                </Typography>
                <Chip
                  size="small"
                  label={meta.label}
                  color={meta.severity}
                  variant={meta.status === "live" ? "filled" : "outlined"}
                  sx={{ fontWeight: 900 }}
                />
              </Stack>
              {!compact || compactActions ? (
                <Typography variant="body2" sx={{ mt: 0.2, color: "rgba(25,36,47,.62)" }}>
                  {meta.description}
                </Typography>
              ) : null}
            </Box>
          </Stack>

          {compactActions ? (
            <Stack direction="row" spacing={.7} sx={{ width: "100%" }}>
              <Button
                type="button"
                fullWidth
                size="small"
                variant={primaryCompactAction === "open" ? "contained" : "outlined"}
                startIcon={
                  primaryCompactAction === "open"
                    ? <OpenInNewRoundedIcon />
                    : primaryCompactAction === "retry"
                      ? (busy === "retry" ? <CircularProgress size={15} /> : <ReplayRoundedIcon />)
                      : (busy === "refresh" ? <CircularProgress size={15} /> : <RefreshRoundedIcon />)
                }
                disabled={
                  primaryCompactAction === "open"
                    ? !publicUrl
                    : !key || Boolean(busy)
                }
                onClick={
                  primaryCompactAction === "open"
                    ? openWebsite
                    : primaryCompactAction === "retry"
                      ? retry
                      : () => refresh()
                }
                sx={{
                  minHeight: 40,
                  borderRadius: 2.1,
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: primaryCompactAction === "open" ? BRAND.orange : undefined,
                }}
              >
                {primaryCompactLabel}
              </Button>
              {showCompactMore ? (
                <>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    aria-label="Más acciones de publicación"
                    aria-haspopup="menu"
                    aria-expanded={actionsAnchor ? "true" : undefined}
                    onClick={(event) => setActionsAnchor(event.currentTarget)}
                    sx={{
                      minWidth: 44,
                      width: 44,
                      minHeight: 40,
                      p: 0,
                      borderRadius: 2.1,
                      color: BRAND.dark,
                      borderColor: "rgba(25,36,47,.14)",
                    }}
                  >
                    <MoreHorizRoundedIcon />
                  </Button>
                  <Menu
                    anchorEl={actionsAnchor}
                    open={Boolean(actionsAnchor)}
                    onClose={closeActions}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                      sx: {
                        mt: .55,
                        width: 232,
                        maxWidth: "calc(100vw - 24px - env(safe-area-inset-left) - env(safe-area-inset-right))",
                        borderRadius: 2.4,
                        border: "1px solid rgba(25,36,47,.08)",
                        boxShadow: "0 16px 42px rgba(25,36,47,.16)",
                      },
                    }}
                    MenuListProps={{ "aria-label": "Acciones de publicación" }}
                  >
                    {primaryCompactAction !== "open" && publicUrl ? (
                      <MenuItem onClick={() => { closeActions(); openWebsite(); }} sx={{ minHeight: 44, fontWeight: 850 }}>
                        Abrir web
                      </MenuItem>
                    ) : null}
                    {primaryCompactAction !== "refresh" ? (
                      <MenuItem disabled={!key || Boolean(busy)} onClick={runRefresh} sx={{ minHeight: 44, fontWeight: 850 }}>
                        Actualizar estado
                      </MenuItem>
                    ) : null}
                    {retryAllowed && primaryCompactAction !== "retry" ? (
                      <MenuItem disabled={!key || Boolean(busy)} onClick={runRetry} sx={{ minHeight: 44, fontWeight: 850, color: BRAND.orange }}>
                        Reintentar publicación
                      </MenuItem>
                    ) : null}
                  </Menu>
                </>
              ) : null}
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
              <Button
                type="button"
                size="small"
                variant={meta.status === "live" ? "contained" : "outlined"}
                startIcon={<OpenInNewRoundedIcon />}
                disabled={!publicUrl}
                onClick={openWebsite}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: meta.status === "live" ? BRAND.orange : undefined,
                }}
              >
                Abrir web
              </Button>
              <Button
                type="button"
                size="small"
                startIcon={busy === "refresh" ? <CircularProgress size={15} /> : <RefreshRoundedIcon />}
                disabled={!key || Boolean(busy)}
                onClick={() => refresh()}
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}
              >
                Actualizar
              </Button>
              {retryAllowed ? (
                <Button
                  type="button"
                  size="small"
                  startIcon={busy === "retry" ? <CircularProgress size={15} /> : <ReplayRoundedIcon />}
                  disabled={!key || Boolean(busy)}
                  onClick={retry}
                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.orange }}
                >
                  Reintentar
                </Button>
              ) : null}
            </Stack>
          )}
        </Stack>

        {originalDomain ? (
          <Box>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 900, color: "rgba(25,36,47,.50)" }}>
              DOMINIO ORIGINAL DEL PROYECTO
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={openWebsite}
              sx={{
                display: "inline",
                p: 0,
                mt: 0.2,
                border: 0,
                bgcolor: "transparent",
                color: BRAND.dark,
                font: "inherit",
                fontWeight: 850,
                cursor: "pointer",
                overflowWrap: "anywhere",
                textAlign: "left",
                "&:hover": { color: BRAND.orange, textDecoration: "underline" },
              }}
            >
              {originalDomain}
            </Typography>
          </Box>
        ) : null}

        {!compact && renderStatus ? (
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.48)" }}>
            Estado técnico de Render: {renderStatus}
          </Typography>
        ) : null}

        {deploymentError ? <Alert severity="error">{deploymentError}</Alert> : null}
        {localError ? <Alert severity="error" onClose={() => setLocalError("")}>{localError}</Alert> : null}
        </Stack>
      </Paper>

      {showCustomDomain ? (
        <ProjectCustomDomainCard
          project={project}
          canManage={canManageDomain}
          onProjectChange={applyResult}
        />
      ) : null}
    </Stack>
  );
}
